import { logger } from "@scott/logger";
import { BlockErrorBoundary } from "@scott/ui/blocks/block-error-boundary";
import { BlockPlaceholder } from "@scott/ui/blocks/block-placeholder";
import type React from "react";
import { type ReactNode, Suspense } from "react";
import { AccordionBlockPreview } from "@/components/blocks/content-blocks/accordion-block.preview";
import { ContentSliderBlockPreview } from "@/components/blocks/content-blocks/content-slider-block.preview";
import { GeometryDataBlock } from "@/components/blocks/content-server-blocks/geometry-data-block";
import { GeometryDataBlockPreview } from "@/components/blocks/content-server-blocks/geometry-data-block.preview";
import { ProductListingBlock } from "@/components/blocks/content-server-blocks/product-listing-block";
import { ProductListingBlockPreview } from "@/components/blocks/content-server-blocks/product-listing-block.preview";
import { ServerBlockSkeleton } from "@/components/blocks/content-server-blocks/server-block-skeleton";
import { Claim } from "@/scott/ui/blocks/claim/claim";
import { ClaimPreview } from "@/scott/ui/blocks/claim/claim.preview";
import { Counter } from "@/scott/ui/blocks/counter/counter-section";
import { CounterPreview } from "@/scott/ui/blocks/counter/counter-section.preview";
import { HeroSection } from "@/scott/ui/blocks/hero-section/hero-section";
import { HeroSectionPreview } from "@/scott/ui/blocks/hero-section/hero-section.preview";
import { HeroSectionSlider } from "@/scott/ui/blocks/hero-section/hero-section-slider";
import { ImageArrangement } from "@/scott/ui/blocks/image-arrangement/image-arrangement";
import { ImageArrangementPreview } from "@/scott/ui/blocks/image-arrangement/image-arrangement.preview";
import { PoiImageHotspot } from "@/scott/ui/blocks/poi-image-hotspot/poi-image-hotspot";
import { PoiImageHotspotPreview } from "@/scott/ui/blocks/poi-image-hotspot/poi-image-hotspot.preview";
import { Quote } from "@/scott/ui/blocks/quote/quote";
import { QuotePreview } from "@/scott/ui/blocks/quote/quote.preview";
import { Slider } from "@/scott/ui/blocks/slider/slider";
import { SliderPreview } from "@/scott/ui/blocks/slider/slider.preview";
import { TaglineSection } from "@/scott/ui/blocks/tagline-section/tagline-section";
import { TaglineSectionPreview } from "@/scott/ui/blocks/tagline-section/tagline-section.preview";
import { TeaserSection } from "@/scott/ui/blocks/teaser-section/teaser-section";
import { TeaserSectionPreview } from "@/scott/ui/blocks/teaser-section/teaser-section.preview";
import { TextImage } from "@/scott/ui/blocks/text-image/text-image";
import { TextImagePreview } from "@/scott/ui/blocks/text-image/text-image.preview";
import { TextIntroSection } from "@/scott/ui/blocks/text-intro-section/text-intro-section";
import { TextSection } from "@/scott/ui/blocks/text-section/text-section";
import { TextSectionPreview } from "@/scott/ui/blocks/text-section/text-section.preview";
import type {
  PageBlock,
  SectionGalleryPageBlock,
  UnknownPageBlock,
} from "@/types/page-block";
import type { PageContext } from "@/types/page-context";
import { AccordionBlock } from "./accordion-block";
import { ContentSliderBlock } from "./content-slider-block";
import { MediaSection } from "./media-section";
import { MediaSectionPreview } from "./media-section.preview";
import { TabContainerBlock } from "./tab-container-block";

type ContentBlocksProps = PageContext & {
  blocks?: PageBlock[];
  serverSlots?: Map<string, ReactNode>;
  disablePreviewInspector?: boolean;
};

type ComponentEntry =
  // biome-ignore lint/suspicious/noExplicitAny: intentional registry pattern
  | React.ComponentType<any>
  // biome-ignore lint/suspicious/noExplicitAny: intentional registry pattern
  | [React.ComponentType<any>, React.ComponentType<any>];

export const serverComponentMap: Record<string, ComponentEntry> = {
  GeometryTable: [GeometryDataBlock, GeometryDataBlockPreview],
  ProductListing: [ProductListingBlock, ProductListingBlockPreview],
};

const componentMap: Record<string, ComponentEntry> = {
  SectionQuote: [Quote, QuotePreview],
  SectionHero: [HeroSection, HeroSectionPreview],
  HeroSectionSlider,
  SectionSlider: [Slider, SliderPreview],
  SectionClaim: [Claim, ClaimPreview],
  SectionTagline: [TaglineSection, TaglineSectionPreview],
  SectionTeaser: [TeaserSection, TeaserSectionPreview],
  SectionText: [TextSection, TextSectionPreview],
  SectionIntroText: TextIntroSection,
  SectionCounter: [Counter, CounterPreview],
  SectionTextImage: [TextImage, TextImagePreview],
  SectionGallery: [ImageArrangement, ImageArrangementPreview],
  SectionHotspot: [PoiImageHotspot, PoiImageHotspotPreview],
  SectionMedia: [MediaSection, MediaSectionPreview],
  AccordionD2C: [AccordionBlock, AccordionBlockPreview],
  TabContainerD2C: TabContainerBlock,
  ContentSliderD2C: [ContentSliderBlock, ContentSliderBlockPreview],
  ...serverComponentMap,
};

/**
 * Server blocks fetch their own data. A Suspense boundary per block lets the
 * rest of the page stream immediately while each block shows its skeleton.
 */
function withServerBlockSuspense(block: PageBlock, children: ReactNode) {
  if (!Object.hasOwn(serverComponentMap, block.__typename)) {
    return children;
  }

  return (
    <Suspense fallback={<ServerBlockSkeleton block={block} />}>
      {children}
    </Suspense>
  );
}

function resolveComponent(entry: ComponentEntry, isPreview: boolean) {
  if (Array.isArray(entry) && entry.length === 2) {
    return isPreview ? entry[1] : entry[0];
  }

  return entry;
}

function renderUnknownBlock(block: UnknownPageBlock): ReactNode {
  const reason = block.reason ?? "Unsupported content block";

  logger.warn("Unsupported content block type", {
    blockId: block.id,
    __typename: block.__typename,
    reason,
  });

  if (process.env.NODE_ENV === "development") {
    return (
      <BlockPlaceholder
        data={{ ...block, __typename: block.__typename ?? "Unknown" }}
        key={block.__typename + block.id}
      />
    );
  }

  return null;
}

function isSectionGalleryBlock(
  block: PageBlock
): block is SectionGalleryPageBlock {
  return block.__typename === "SectionGallery";
}

function resolveBlockProps(
  block: PageBlock,
  pageContext: PageContext["pageContext"]
): PageBlock {
  if (!isSectionGalleryBlock(block)) {
    return block;
  }

  return {
    ...block,
    readMoreLabel:
      block.readMoreLabel ??
      pageContext.microcopies?.imageArrangementReadMoreLabel,
  };
}

export function ContentBlocks(props: Readonly<ContentBlocksProps>) {
  if (!props.blocks?.length) {
    return null;
  }

  const effectiveServerSlots =
    props.serverSlots ?? props.pageContext.serverSlots;

  return props.blocks.map((block) => {
    const sysId = block.sys?.id;
    const serverSlot = sysId ? effectiveServerSlots?.get(sysId) : undefined;

    if (serverSlot !== undefined) {
      return (
        <BlockErrorBoundary blockName={block.__typename} key={`${block.id}`}>
          {withServerBlockSuspense(block, serverSlot)}
        </BlockErrorBoundary>
      );
    }

    const entry = componentMap[block.__typename];
    const usePreview =
      props.pageContext.draft.isEnabled && !props.disablePreviewInspector;
    const Component = entry ? resolveComponent(entry, usePreview) : undefined;

    if (Component) {
      const blockProps = resolveBlockProps(block, props.pageContext);

      return (
        <BlockErrorBoundary blockName={block.__typename} key={`${block.id}`}>
          {withServerBlockSuspense(
            block,
            <Component
              {...blockProps}
              country={props.pageContext.params?.country}
              language={props.pageContext.params?.lang}
              pageContext={props.pageContext}
            />
          )}
        </BlockErrorBoundary>
      );
    }

    return renderUnknownBlock(block as UnknownPageBlock);
  });
}
